import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { DSSetup, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import themeClasses from 'webapp/helpers/theme-classes';
import BlockFiltersListItemComponent from 'webapp/components/block/filters/list-item';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type { Block, DataSelect, FilterItem } from 'webapp/models-custom';

interface Args {
	blockM: Block;
	model: DataSelect;
	theme?: string;
	placeholder?: string;
	isDisabled?: boolean;
	origin?: string;
	onFiltersChange?: (filters: DSSetup.MetricSetupFiltersData | null) => void;
}

export default class BlockSettingsFiltersComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup() {
		return this.block.blockSetup;
	}

	get metricSetup() {
		return this.args.model.metricSetup;
	}

	get filterGroup() {
		return this.metricSetup.dimensionFilterGroup;
	}

	get items() {
		return this.filterGroup?.filterItems || [];
	}

	get placeholder() {
		return this.args.placeholder || 'Select filters';
	}

	@action
	openFilters(): void {
		this.openFilterModal();
	}

	@action
	editFilter(item: FilterItem): void {
		this.openFilterModal(item);
	}

	@action
	removeFilter(item: FilterItem): void {
		this.analyticsService.event(TrackingEvent.FILTER_DATA_REMOVED);
		this.filterGroup?.removeFilterItem(item);
		this.updateFilters(this.metricSetup.filtersData);
	}

	private updateFilters(filters: DSSetup.MetricSetupFiltersData | null) {
		this.blockSetup.changeFilters(this.args.model, filters);
		this.args.onFiltersChange?.(filters);
	}

	private openFilterModal(editItem?: FilterItem) {
		this.mmMetricsService.openFiltersPopup(
			{
				metricSetup: this.args.model.metricSetup,
				editItem,
				origin: this.args.origin,
				apply: (
					_dimensions: string[] | undefined,
					filters: DSSetup.MetricSetupFiltersData | null,
				) => {
					// ignoring dimensions bcos from block settings we only show filters
					// dimensions are only used in master filter popup
					this.updateFilters(filters);
				},
			},
			this.args.theme,
		);
	}
	<template>
		<div class="ms-filters {{if @isDisabled 'ms-filters--disabled'}}">
			<a class="action-add" {{on "click" this.openFilters}}>
				<CommonUiIconComponent @name="Plus" @size="sm" />
			</a>

			<div class="ms-filters-items {{themeClasses @theme}}">

				{{#each this.items as |item index|}}
					<BlockFiltersListItemComponent
						@model={{item}}
						@logicalOperatorText={{if index this.filterGroup.logicalOperatorText}}
						@metricDimensions={{this.metricSetup.metricDimensions}}
						@edit={{fn this.editFilter item}}
						@remove={{fn this.removeFilter item}}
					/>
				{{else}}
					<a class="placeholder" {{on "click" this.openFilters}}>
						{{this.placeholder}}
					</a>
				{{/each}}

			</div>
		</div>
	</template>
}
